/**
 * Year → URL Sync Hook
 * 
 * Synchronizes the global year filter with URL query parameters.
 * Works with wouter routing - does not use react-router.
 * 
 * Responsibilities:
 * 1. Read `year` from URL query params on first mount
 * 2. If no param → set URL to current global year
 * 3. When global year changes → sync it back into the URL
 * 4. Does NOT cause infinite loops
 * 5. Does NOT mutate other query parameters
 */
import { useEffect, useRef } from 'react';
import { useYear, useSetYear } from '@/state/yearStore';

const YEAR_PARAM = 'year';

/**
 * Parse year from URL search params.
 * Returns null if not present or invalid.
 */
function getYearFromUrl(): number | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const yearStr = params.get(YEAR_PARAM);
    if (yearStr) {
      const parsed = parseInt(yearStr, 10);
      if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
        return parsed;
      }
    }
  } catch (e) {
    // SSR or URL parsing error
  }
  return null;
}

/**
 * Update URL with year parameter without mutating other params.
 * Uses replaceState to avoid polluting browser history.
 */
function setYearInUrl(year: number): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(YEAR_PARAM, String(year));
    window.history.replaceState({}, '', url.toString());
  } catch (e) {
    // SSR or URL manipulation error
  }
}

/**
 * Hook to sync year between global store and URL.
 * 
 * Usage:
 * ```tsx
 * function MyPage() {
 *   const year = useYear();
 *   useUrlYear(); // Call this on pages that need URL sync
 *   // ...
 * }
 * ```
 */
export function useUrlYear(): void {
  const year = useYear();
  const setYear = useSetYear();
  const initialized = useRef(false);
  const lastUrlYear = useRef<number | null>(null);

  // On mount: read year from URL and sync to store
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const urlYear = getYearFromUrl();
    
    if (urlYear !== null) {
      // URL has a year - sync to store if different
      if (urlYear !== year) {
        setYear(urlYear);
      }
      lastUrlYear.current = urlYear;
    } else {
      // No year in URL - set it from store
      setYearInUrl(year);
      lastUrlYear.current = year;
    }
  }, []); // Empty deps - run only on mount

  // When store year changes, sync to URL
  useEffect(() => {
    if (!initialized.current) return;
    
    // Only update URL if year actually changed (avoid loops)
    if (lastUrlYear.current !== year) {
      setYearInUrl(year);
      lastUrlYear.current = year;
    }
  }, [year]);
}

export default useUrlYear;



